import { LoadingState } from './../../../../../@core/api/models';
import { InitiativesService } from '../../../../../@core/utils/initiatives.service';
import { PerimeterEnergyTrajectoryService } from '../../../../../@core/utils';
import { ChartsService } from '../../../../../@core/utils/charts.service';
import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { NbCardModule, NbSpinnerModule, NbButtonModule, NbInputModule, NbSelectModule, NbOptionModule, NbAccordionModule, NbDialogRef, NbToggleModule, NbDialogService, NbAlertModule } from '@nebular/theme';
import { KPI } from '../../../../../@core/api';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, AbstractControl, ValidationErrors } from '@angular/forms';
import { ScopeService, toLoadingState } from '../../../../../@core/utils/scope.service';
import { AsyncPipe, LowerCasePipe, NgStyle, NgTemplateOutlet } from '@angular/common';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { first, Observable, Subscription } from 'rxjs';
import { SideFormDialogComponent } from '../../side-form-dialog/side-form-dialog.component';
import { IntegerOnlyDirective } from '../../../../../@core/directives';
import { map } from 'rxjs/operators';
import * as R from 'ramda';
import { NegativeOnlyDirective } from '../../../../directives';
import { gainValidator, GainFieldsVisibility } from '../initiative-gain.validator';

@Component({
  selector: 'app-initiative-gains-form',
  templateUrl: './initiative-gains-form.component.html',
  styleUrls: ['../../base-form/base-form.component.scss'],
  styles: [`
    .add-gain, [nbButton].appearance-ghost.status-basic:hover {
      background-color: #3a546b !important;
      color: #fff !important;
      width: 45px;
      height: 45px;
      border-radius: 50%;
      box-shadow: 0px 1px 3px 0px #1e2c39;
      position: absolute;
      right: 5px;
      top: 0;
      bottom: 0;
      margin: auto;
    }

    .add-gain:hover {
      color: #3a546b !important;
    }

    nb-accordion-item {
      margin-bottom: 15px;
    }

    .accordion-button {
      position: absolute;
      top: 0;
      bottom: 0;
      right: 30px;
      margin: auto;
      height: 30px;
      width: 30px;
      background-color: transparent !important;
      color: #222b45 !important;

      &:hover {
        color: #222b45 !important;
      }
    }

    nb-card-header {
      box-shadow: 0 0 5px 0 #c6c6c6 !important;
      border-bottom: none !important;

      .back-and-title {
        display: flex;
        align-items: center;
        width: 100%;
        position: relative;
        .title {
          position: static !important;
          padding-left: 60px;
          padding-right: 60px;
        }
      }
    }

    .building-consumption-alert {
      background-color: #ff8b8b !important;
      border-color: #ff8b8b !important;
      font-weight: normal !important;
      text-align: center;
      margin-bottom: 0;
    }

  `],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NbCardModule,
    NbSpinnerModule,
    NbButtonModule,
    NbInputModule,
    NbSelectModule,
    NbOptionModule,
    NbAccordionModule,
    AsyncPipe,
    TranslateModule,
    NgTemplateOutlet,
    NgStyle,
    NbToggleModule,
    NbAlertModule,
    LowerCasePipe,
    IntegerOnlyDirective,
    NegativeOnlyDirective,
  ],
})
export class InitiativeGainsFormComponent implements OnInit, OnDestroy {

  @Input({required: true}) initiativeForm!: FormGroup;
  @Input({required: false}) initiativeId: number | null = null;
  @Input() siteId: number | null = null;
  @Input() inWizard: boolean = false;

  public title = this.translate.instant('Energy/Carbon gains');
  private subscriptions: Subscription[] = [];
  private petrajLoaded = false;
  private petrajEnergyTypeIds: Set<number> = new Set();

  public energyTypes$: Observable<LoadingState<KPI.InitiativeEnergyType[]>> = this.initiativesService.initiativeEnergyTypes$.pipe(
    toLoadingState<KPI.InitiativeEnergyType[]>(),
  );
  public gainOrigins$: Observable<LoadingState<KPI.GainOrigin[]>> = this.initiativesService.gainOrigins$.pipe(
    toLoadingState<KPI.GainOrigin[]>(),
  );
  public gainPrecisions$: Observable<LoadingState<KPI.GainPrecision[]>> = this.initiativesService.gainPrecisions$.pipe(
    toLoadingState<KPI.GainPrecision[]>(),
  );
  public refrigerantTypes$: Observable<LoadingState<KPI.RefrigerantType[]>> = this.scopeService.refrigerantTypes$.pipe(
    toLoadingState<KPI.RefrigerantType[]>(),
  );
  public fossilEnergyTypes$: Observable<LoadingState<KPI.InitiativeEnergyType[]>> = this.initiativesService.initiativeEnergyTypes$.pipe(
    map(types => types.filter(t => !t.is_electricity)),
    toLoadingState<KPI.InitiativeEnergyType[]>(),
  );
  private initiativeTypes!: KPI.InitiativeType[];
  private initialGainsSnapshot: any[] = [];
  /** Latest declared consumption (kWh/year) of the initiative's own building, keyed by energy_type_id. */
  private buildingEnergyConsumption: Map<number, number> = new Map();

  constructor(
    private translate: TranslateService,
    private initiativesService: InitiativesService,
    private formBuilder: FormBuilder,
    private nbDialogRef: NbDialogRef<InitiativeGainsFormComponent>,
    private scopeService: ScopeService,
    private dialogService: NbDialogService,
    private perimeterEnergyTrajectoryService: PerimeterEnergyTrajectoryService,
    private chartsService: ChartsService
  ) { }

  public ngOnInit(): void {
    this.initiativesService.initiativeTypes$.pipe(first()).subscribe((types) => {
      this.initiativeTypes = types;

      this.gains.controls.forEach((gainControl, _) => {
        const gainGroup = gainControl as FormGroup;
        gainGroup.setValidators([gainValidator(this.gainFieldsVisibility)]);
        gainGroup.updateValueAndValidity({ emitEvent: false });
      });
    });

    if (this.siteId) {
      this.subscriptions.push(
        this.perimeterEnergyTrajectoryService.getPerimeterEnergyTrajectories(this.siteId).subscribe(trajs => {
          this.petrajEnergyTypeIds = new Set(
            trajs
              .filter(t => t.trajectory_records?.some(r => r.consumption != null))
              .map(t => t.energy_type)
          );
          this.petrajLoaded = true;
        })
      );

      const perimeterId = this.initiativeForm.get('perimeter')?.value;
      this.subscriptions.push(
        this.chartsService.getSiteConsumption(this.siteId).subscribe(siteConsumption => {
          this.buildingEnergyConsumption = this.computeBuildingEnergyConsumption(siteConsumption, perimeterId);
        })
      );
    }

    if (!this.gains.length) {
      // abandon energy type requires 2 gains, so add 2 empty gains in this case, 1 otherwise
      const numberOfGainsToAdd = this.isAbandonType ? 2 : 1;
      this.addGain(numberOfGainsToAdd);
    }

    this.gains.setValidators([
      this.duplicateEnergyTypeValidator.bind(this),
      this.gainTotalityValidator.bind(this),
    ]);
    this.gains.updateValueAndValidity();

    this.subscriptions.push(
      this.gains.valueChanges.subscribe(() => {
        this.gains.updateValueAndValidity({ emitEvent: false });
      })
    );

    this.subscribeToEnergyTypeChanges();

    if (!this.inWizard) {
      this.subscriptions.push(
        this.nbDialogRef.onBackdropClick.subscribe(() => {
          this.confirmClose();
        })
      )
    }

    this.captureInitialGainsSnapshot();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.removeEmptyGains();
  }

  public get gains() {
    return this.initiativeForm.get("gains") as FormArray & {
      controls: FormGroup[];
    };
  }

  public addGain(numberOfGain: number = 1): void {
    let initiativeId = this.initiativeId;
    if (!initiativeId && this.initiativeForm) {
      const initiative = (this.initiativeForm as any).initiative;
      if (initiative?.id) {
        initiativeId = initiative.id;
      }
    }

    const gain = {
      id: null,
      initiative_id: initiativeId,
      energy_type_id: null,
      refrigerant_type_id: null,
      gain_origin_id: null,
      gain_precision_id: null,
      theoretical_savings_full_year: null,
      theoretical_savings_first_year: null,
      theoretical_greenhouse_gas_savings_full_year: null,
      theoretical_greenhouse_gas_savings_first_year: null,
      real_savings: null,
      real_greenhouse_gas_savings: null,
      full_stop: false,
    };
    const gainsToAdd = Math.max(0, Math.floor(numberOfGain || 0));
    for (let i = 0; i < gainsToAdd; i++) {
      if (!this.canAddGain) {
        break;
      }
      this.addGainInternal({ ...gain });
    }
  }

  private addGainInternal(gain: any): void {
    const iniType = this.initiativeType;
    const gainIndex = this.gains.length; // index before pushing
    const newGainGroup = this.formBuilder.group(gain, {
      validators: [gainValidator(this.gainFieldsVisibility)],
    });

    this.gains.push(newGainGroup);
    this.gains.markAsDirty();

    this.subscribeToEnergyTypeControl(newGainGroup);

    // For QUIT_FOSSIL: ensure first gain full_stop is always true
    if (iniType?.gain_type === "QUIT_FOSSIL" && gainIndex === 0) {
      this.setupFirstGainForAbandon();
    }
  }

  public removeGain(index: number) {
    if (index === 0 && this.gains.length === 1) {
      this.gains.controls[0].reset();
      this.gains.markAsDirty();
    } else {
      this.gains.removeAt(index);
      this.gains.markAsDirty();
    }
    this.gains.updateValueAndValidity({ emitEvent: false });
  }

  private subscribeToEnergyTypeChanges(): void {
    this.gains.controls.forEach((gain) => {
      this.subscribeToEnergyTypeControl(gain as FormGroup);
    });
  }

  private subscribeToEnergyTypeControl(gain: FormGroup): void {
    const energyTypeControl = gain.get('energy_type_id');
    if (energyTypeControl) {
      this.subscriptions.push(
        energyTypeControl.valueChanges.subscribe(() => {
          this.gains.updateValueAndValidity({ emitEvent: false });
        })
      );
    }
  }

  public get showRefrigerantInput() {
    if (!this.initiativeTypes) return false;
    const iniType = this.initiativeType;
    return iniType?.gain_type === "CARBON";
  }

  public showEnergyInput(type: "ENERGY_SELECT" | "ENERGY_INPUT") {
    if (!this.initiativeTypes) return false;
    const iniType = this.initiativeType;
    return type === "ENERGY_SELECT"
      ? iniType?.gain_type === "ENERGY" || iniType?.gain_type === "QUIT_FOSSIL"
      : iniType?.gain_type === "ENERGY";
  }

  /** Show the kWh amount input: always for ENERGY type, only for 2nd gain of QUIT_FOSSIL. */
  public showAmountInput(gainIndex: number): boolean {
    if (!this.initiativeTypes) return false;
    const iniType = this.initiativeType;
    if (iniType?.gain_type === "ENERGY") return true;
    if (iniType?.gain_type === "QUIT_FOSSIL" && gainIndex === 1) return true;
    return false;
  }

  private gainFieldsVisibility = (gain: AbstractControl): GainFieldsVisibility => {
    if (this.isAbandonType) {
      return { energyType: false, energyAmount: false, refrigerantType: false, co2Amount: false };
    }

    const index = this.gains.controls.indexOf(gain as FormGroup);

    return {
      energyType: this.showEnergyInput('ENERGY_SELECT'),
      energyAmount: this.showAmountInput(index),
      refrigerantType: false,
      co2Amount: this.showRefrigerantInput,
    };
  };

  /**
   * Latest declared consumption record (highest year) per energy type, for the
   * initiative's own building (perimeter) only — not summed across the whole site.
   */
  private computeBuildingEnergyConsumption(
    siteConsumption: KPI.SiteConsumption,
    perimeterId: number | null,
  ): Map<number, number> {
    const consumptionByEnergyType = new Map<number, number>();
    const perimeter = (siteConsumption?.perimeters || []).find(p => p.id === perimeterId);
    (perimeter?.trajectories || []).forEach(trajectory => {
      if (trajectory.energy_type && trajectory.perimeter_consumptions?.length) {
        const lastConsumption = R.reduce(
          (latest: KPI.PerimeterTrajectoryConsumption, current: KPI.PerimeterTrajectoryConsumption) =>
            current.year > latest.year ? current : latest,
          trajectory.perimeter_consumptions[0],
          trajectory.perimeter_consumptions,
        );
        consumptionByEnergyType.set(trajectory.energy_type.id, lastConsumption.consumption);
      }
    });
    return consumptionByEnergyType;
  }

  /** True when this gain's kWh value exceeds its own building's consumption for the same energy type. */
  public gainExceedsBuildingConsumption(gainIndex: number): boolean {
    const gain = this.gains.controls[gainIndex];
    if (!gain) {
      return false;
    }

    const energyTypeId = gain.get('energy_type_id')?.value;
    const gainValue = Number(gain.get('theoretical_savings_full_year')?.value) || 0;
    if (!energyTypeId || gainValue <= 0) {
      return false;
    }

    const buildingConsumption = this.buildingEnergyConsumption.get(energyTypeId);
    if (!buildingConsumption) {
      return false;
    }

    return gainValue > buildingConsumption;
  }

  public get isAbandonType() {
    if (!this.initiativeTypes) return false;
    const iniType = this.initiativeType;
    return iniType?.gain_type === "QUIT_FOSSIL";
  }

  /** Prevent adding a 3rd gain when type is QUIT_FOSSIL. */
  public get canAddGain(): boolean {
    return !(this.isAbandonType && this.gains.length >= 2);
  }

  /**
   * Ensure first gain's full_stop is always true for QUIT_FOSSIL.
   * Called on init and when adding the first gain.
   */
  private setupFirstGainForAbandon(): void {
    if (this.gains.controls.length > 0) {
      const fullStopControl = (this.gains.controls[0] as FormGroup).get('full_stop');
      if (fullStopControl && fullStopControl.value !== true) {
        fullStopControl.setValue(true, { emitEvent: false });
        this.gains.markAsDirty();
      }
    }
  }

  private get initiativeType(): KPI.InitiativeType | null {
    if (!this.initiativeTypes || !this.initiativeForm) {
      return null;
    }
    const initiativeTypeId = this.initiativeForm.get("initiative_type")?.value;
    if (!initiativeTypeId) {
      return null;
    }
    return R.find(type => type.id === initiativeTypeId, this.initiativeTypes) ?? null;
  }

  /**
   * Remove empty gains from the form array
   * This is necessary because the form array is not updated when a gain is removed
   * Do it in reverse order to avoid index issues
   */
  private removeEmptyGains() {
    R.reverse(R.range(0, this.gains.controls.length)).forEach((index) => {
      if (this.gains.controls[index].invalid) {
        this.gains.removeAt(index);
      }
    });
  }

  /**
   * Apart from having single control in the gain itself
   * @param control
   * @returns
   */
  private gainTotalityValidator(control: AbstractControl): ValidationErrors | null {
    const gainsArray = control as FormArray;
    if (!gainsArray || !gainsArray.controls) {
      return null;
    }

    const iniType = this.initiativeType;
    if (iniType?.gain_type === "QUIT_FOSSIL") {
      const hasFullStopGain = gainsArray.controls.some(gain => gain.get('full_stop')?.value === true);
      if (!hasFullStopGain) {
        return { fullStopRequired: true };
      }
    }
    return null
  }

  private duplicateEnergyTypeValidator(control: AbstractControl): ValidationErrors | null {
    const gainsArray = control as FormArray;
    if (!gainsArray || !gainsArray.controls) {
      return null;
    }

    if (!this.showEnergyInput('ENERGY_SELECT')) {
      return null;
    }

    const energyTypeIds: (number | null)[] = R.pipe(
      R.map((gain: AbstractControl) => gain.get('energy_type_id')?.value),
      R.filter((value: any) => value != null && value !== ''),
    )(gainsArray.controls);

    if (R.length(energyTypeIds) !== R.length(R.uniq(energyTypeIds))) {
      return { duplicateEnergyType: true };
    }

    return null;
  }

  public hasDuplicateEnergyType(gainIndex: number): boolean {
    if (!this.showEnergyInput('ENERGY_SELECT')) {
      return false;
    }

    const currentGain = this.gains.controls[gainIndex];
    if (!currentGain) {
      return false;
    }

    const currentEnergyTypeId = currentGain.get('energy_type_id')?.value;
    if (currentEnergyTypeId == null || currentEnergyTypeId === '') {
      return false;
    }

    const duplicateCount = R.length(R.filter((gain: AbstractControl) => {
      const energyTypeId = gain.get('energy_type_id')?.value;
      return energyTypeId === currentEnergyTypeId && energyTypeId != null && energyTypeId !== '';
    }, this.gains.controls));

    return duplicateCount > 1;
  }

  // Only list energy types that actually have a PETraj (consumption trajectory) for the site.
  // Filtering the source array (rather than wrapping each <nb-option> in an @if) avoids
  // interfering with how nb-select collects its options via ContentChildren.
  visibleEnergyTypes(energyTypes: KPI.InitiativeEnergyType[], currentGainIndex: number): KPI.InitiativeEnergyType[] {
    return (energyTypes || []).filter((energyType) => this.isEnergyTypeVisible(energyType.id, currentGainIndex));
  }

  private isEnergyTypeVisible(energyTypeId: number, currentGainIndex: number): boolean {
    if (!energyTypeId) {
      return true;
    }

    // Never hide the value already selected for this gain, even if its trajectory
    // later disappeared, otherwise the select would show an empty option.
    const currentGain = this.gains.controls[currentGainIndex];
    if (currentGain?.get('energy_type_id')?.value === energyTypeId) {
      return true;
    }

    // Only filter once PETraj has actually loaded some data for the site — otherwise
    // (site not yet known, or the site genuinely has no declared consumption) every
    // type would look "without trajectory" and the whole list would wrongly disappear.
    if (!this.petrajLoaded || this.petrajEnergyTypeIds.size === 0) {
      return true;
    }

    // PETraj restriction:
    // - QUIT_FOSSIL 2nd gain (index 1): no restriction — any energy type may be used
    //   (the new alternative energy might not have a trajectory yet)
    // - All other cases: restrict to energy types that already have a PETraj
    const shouldCheckPetraj = !(this.isAbandonType && currentGainIndex === 1);
    if (shouldCheckPetraj && !this.petrajEnergyTypeIds.has(energyTypeId)) {
      return false;
    }

    return true;
  }

  isEnergyTypeDisabled(energyTypeId: number, currentGainIndex: number): boolean {
    if (!energyTypeId) {
      return false;
    }

    // Duplicate check: disable energy types already selected in another gain
    for (let i = 0; i < this.gains.length; i++) {
      if (i === currentGainIndex) {
        continue;
      }
      const gain = this.gains.at(i) as FormGroup;
      if (gain) {
        const selectedEnergyTypeId = gain.get('energy_type_id')?.value;
        if (selectedEnergyTypeId === energyTypeId) {
          return true;
        }
      }
    }

    return false;
  }

  public close(): void {
    this.nbDialogRef.close();
  }

  public validate() {
    if (this.gains.valid) {
      this.nbDialogRef.close({
        saved: true
      });
    }
  }

  public hasUnsavedChanges(): boolean {
    return !R.equals(this.initialGainsSnapshot, this.gains.getRawValue());
  }

  private captureInitialGainsSnapshot(): void {
    this.initialGainsSnapshot = R.clone(this.gains.getRawValue());
  }

  public confirmClose(): void {
    if (!this.hasUnsavedChanges()) {
      this.close();
      return;
    }
    this.dialogService.open(
      SideFormDialogComponent,
      {
        autoFocus: false,
        closeOnBackdropClick: false,
        context: {
          title: this.translate.instant('Unregistered changes'),
          content: this.translate.instant('Would you like to exit ?'),
        },
      }
    ).onClose.subscribe((confirmation: boolean) => {
      if (confirmation) {
        this.close();
      } else {
        return
      }
    });
  }
}
